import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { type ApiError, api, unwrap } from '../api/client';
import { OPERATOR, PublicLayout, SITE_URL, SUPPORT_EMAIL } from '../components/PublicLayout';

/** LEG-01..LEG-04: the date the legal text last changed. */
const LAST_UPDATED = '4 November 2026';

const Mail = () => <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <PublicLayout>
      <main className="legal">
        <h1>{title}</h1>
        <p className="muted small">Last updated: {LAST_UPDATED}</p>
        {children}
      </main>
    </PublicLayout>
  );
}

/** LEG-01 */
export function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        This policy explains how {OPERATOR} (“we”, “us”), the operator of AutoDM at{' '}
        <a href={SITE_URL}>{SITE_URL.replace('https://', '')}</a>, collects, uses, shares and
        protects personal data when you use AutoDM, a tool that lets Instagram professional accounts
        automate replies to comments, story replies and direct messages. Questions: <Mail />.
      </p>

      <h2>1. Data we collect</h2>
      <ul>
        <li>
          <strong>Your account:</strong> name, email address, password (stored only as a salted
          hash), workspace name, team members you invite, and billing status.
        </li>
        <li>
          <strong>Your connected Instagram professional account:</strong> Instagram user id,
          username, display name, account type, profile picture URL, and the access token Instagram
          issues to AutoDM (stored encrypted).
        </li>
        <li>
          <strong>Content and conversations on that account:</strong> your posts, reels and stories
          (ids, captions, thumbnails, basic counts), comments and story replies people leave, the
          direct messages exchanged through AutoDM, and the posts you schedule with AutoDM.
        </li>
        <li>
          <strong>Contacts:</strong> for people who interact with your account, their Instagram
          username and id, and anything they choose to give in a conversation (for example a name,
          email or phone number your flow asks for), plus tags you add.
        </li>
        <li>
          <strong>Usage and analytics:</strong> delivery results, clicks on tracked links (time,
          approximate country and city, browser type), and technical logs (IP address, device and
          browser information) used for security and troubleshooting.
        </li>
      </ul>

      <h2>2. How we use Instagram (Meta) data</h2>
      <p>
        AutoDM connects through the Instagram API with Instagram Login and asks only for the
        permissions it uses:
      </p>
      <ul>
        <li>
          <code>instagram_business_basic</code>: to read your profile (id, username, name, picture)
          and list your media so you can choose which posts an automation watches.
        </li>
        <li>
          <code>instagram_business_manage_comments</code>: to receive comments on your media, reply
          to them publicly and send the private reply you configured.
        </li>
        <li>
          <code>instagram_business_manage_messages</code>: to receive story replies and direct
          messages sent to your account and to send the automated or human replies you set up,
          within Instagram’s messaging rules.
        </li>
        <li>
          <code>instagram_business_content_publish</code>: to publish the posts and reels you
          schedule in AutoDM, at the time you choose.
        </li>
      </ul>
      <p>
        We use data received from Meta only to provide AutoDM’s features to the account that
        connected it. We do not sell it, do not use it for advertising, do not build profiles of the
        people who message you, and do not share it with anyone except the service providers listed
        below, who process it on our behalf. Our use complies with the Meta Platform Terms and
        Developer Policies.
      </p>

      <h2>3. Why we process your data (legal bases)</h2>
      <ul>
        <li>To provide the service you signed up for (performance of a contract).</li>
        <li>With your consent, which you give when you connect your Instagram account.</li>
        <li>To keep AutoDM secure, prevent abuse and fix problems (legitimate interests).</li>
        <li>To meet legal, tax and accounting obligations.</li>
      </ul>

      <h2>4. Who we share data with</h2>
      <p>We use these service providers (processors), bound by contracts to protect your data:</p>
      <ul>
        <li>Railway (application hosting) and Supabase (database hosting).</li>
        <li>Meta Platforms, to talk to Instagram on your behalf.</li>
        <li>PostRoutine, where your workspace chooses it as the Instagram connection provider.</li>
        <li>Resend (transactional email) and Stripe (payments), when enabled.</li>
        <li>Cloudflare (DNS and network security).</li>
      </ul>
      <p>
        We disclose data to authorities only where the law requires it. Some providers process data
        outside your country; where they do, we rely on appropriate safeguards.
      </p>

      <h2>5. How long we keep data</h2>
      <p>
        We keep your data while your workspace is active. When you disconnect an Instagram account
        it stops being used; when you delete the account’s data, delete your workspace, or remove
        AutoDM from Instagram, we delete the related data as described in our{' '}
        <Link to="/data-deletion">data deletion page</Link>, normally immediately and in all cases
        within 30 days. Backups roll over within 30 days. We keep billing records as long as the law
        requires.
      </p>

      <h2>6. Security</h2>
      <p>
        Data is encrypted in transit (HTTPS). Instagram access tokens and API keys are encrypted at
        rest with AES-256-GCM, passwords are hashed, access is limited by role, and actions are
        logged. No system is perfectly secure; if a breach affects you, we will notify you and the
        authorities as the law requires.
      </p>

      <h2>7. Your rights</h2>
      <p>
        Under India’s Digital Personal Data Protection Act, 2023, and, where they apply to you, the
        EU/UK GDPR and the California Consumer Privacy Act (CCPA), you may ask to access, correct,
        or delete your personal data, withdraw consent, object to or restrict processing, receive a
        copy of your data, and nominate someone to exercise these rights for you. We do not sell
        personal information. Write to <Mail />; we answer within 30 days. You may also complain to
        your data protection authority (in India, the Data Protection Board of India).
      </p>

      <h2>8. Deleting your data</h2>
      <p>
        You can delete your data at any time. See the{' '}
        <Link to="/data-deletion">data deletion instructions</Link>.
      </p>

      <h2>9. Children</h2>
      <p>AutoDM is not meant for anyone under 18, and we do not knowingly collect their data.</p>

      <h2>10. Cookies</h2>
      <p>
        AutoDM keeps your sign-in in your browser’s local storage and uses no advertising or
        third-party tracking cookies.
      </p>

      <h2>11. Changes</h2>
      <p>
        We will post changes here and update the date above; for significant changes we will email
        workspace owners.
      </p>

      <h2>12. Contact and grievance officer</h2>
      <p>
        {OPERATOR}, Grievance Officer — <Mail />. We acknowledge grievances within 48 hours and
        resolve them within 30 days.
      </p>
    </LegalPage>
  );
}

/** LEG-02 */
export function TermsPage() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        These terms are an agreement between you and {OPERATOR}, the operator of AutoDM. By creating
        an account or using AutoDM you accept them. If you use AutoDM for a business, you accept
        them on its behalf.
      </p>

      <h2>1. The service</h2>
      <p>
        AutoDM lets Instagram professional accounts automate replies to comments, story replies and
        direct messages, manage conversations and contacts, schedule posts and see analytics. It
        works through Meta’s Instagram APIs, so features depend on what Meta allows and may change
        when Meta changes them.
      </p>

      <h2>2. Eligibility and accounts</h2>
      <ul>
        <li>You must be at least 18 and able to enter a binding contract.</li>
        <li>
          You may connect only Instagram accounts you own or are authorised to manage, and you are
          responsible for everyone you invite to your workspace.
        </li>
        <li>Keep your password safe and tell us at once about any unauthorised use.</li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree not to use AutoDM to:</p>
      <ul>
        <li>send spam, unsolicited bulk messages, or messages outside Meta’s messaging windows;</li>
        <li>
          break Instagram’s Terms of Use, Community Guidelines, or the Meta Platform Terms and
          Developer Policies;
        </li>
        <li>
          send unlawful, misleading, harassing, hateful or infringing content, or content that
          collects sensitive data without consent;
        </li>
        <li>
          impersonate anyone, or misrepresent automated messages as personal ones where that
          misleads;
        </li>
        <li>
          probe, overload, reverse-engineer or get around the limits or security of AutoDM or
          Instagram.
        </li>
      </ul>
      <p>
        We may suspend automations or accounts that break these rules, or that Meta asks us to stop.
      </p>

      <h2>4. Your content and data</h2>
      <p>
        You own your content. You give us permission to process it only to run AutoDM for you, as
        described in our <Link to="/privacy">privacy policy</Link>. You are responsible for having
        the right to message the people you contact and for the messages your automations send.
      </p>

      <h2>5. Plans, trials and billing</h2>
      <p>
        Paid plans are billed in advance for the chosen period and renew until cancelled. You can
        cancel at any time; cancellation takes effect at the end of the current period. Except where
        the law requires, fees already paid are not refunded. We will give notice before changing
        prices for your plan.
      </p>

      <h2>6. Availability and changes</h2>
      <p>
        We work to keep AutoDM available but do not guarantee it will be uninterrupted or
        error-free, and we may change or retire features. Instagram outages and API changes are
        outside our control.
      </p>

      <h2>7. Disclaimers and liability</h2>
      <p>
        AutoDM is provided “as is”. To the extent the law allows, we disclaim implied warranties and
        are not liable for indirect, incidental or consequential losses, lost profits, or actions
        Meta takes on your Instagram account. Our total liability for any claim is limited to the
        fees you paid us in the 12 months before it arose.
      </p>

      <h2>8. Indemnity</h2>
      <p>
        You will indemnify {OPERATOR} against claims arising from your content, your messages or
        your breach of these terms.
      </p>

      <h2>9. Termination</h2>
      <p>
        You can stop using AutoDM and delete your workspace at any time. We may suspend or end your
        access if you breach these terms. On termination we delete your data as described on the{' '}
        <Link to="/data-deletion">data deletion page</Link>.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These terms are governed by the laws of India. Courts in India have exclusive jurisdiction,
        without affecting any mandatory consumer rights where you live.
      </p>

      <h2>11. Contact</h2>
      <p>
        {OPERATOR} — <Mail />.
      </p>
    </LegalPage>
  );
}

/** LEG-03 */
export function DataDeletionPage() {
  return (
    <LegalPage title="Data Deletion">
      <p>
        You can ask {OPERATOR} to delete the data AutoDM holds about you at any time, in any of
        these three ways.
      </p>

      <h2>1. Remove AutoDM from Instagram</h2>
      <ol>
        <li>Open Instagram and go to your profile.</li>
        <li>
          Open <strong>Settings and activity</strong> → <strong>Apps and websites</strong> (on some
          versions: <strong>Website permissions</strong> → <strong>Apps and websites</strong>).
        </li>
        <li>
          Find <strong>AutoDM</strong> under <strong>Active</strong> and tap <strong>Remove</strong>
          .
        </li>
      </ol>
      <p>
        Instagram then tells AutoDM automatically. We disconnect the account and, when you ask
        Instagram to delete your data, erase it and give you a confirmation code you can check on
        the status page.
      </p>

      <h2>2. Delete inside AutoDM</h2>
      <ul>
        <li>
          <strong>One Instagram account:</strong> Accounts → choose the account →{' '}
          <strong>Disconnect</strong>.
        </li>
        <li>
          <strong>Everything:</strong> Settings → <strong>Delete workspace</strong>, then type the
          workspace name to confirm. This deletes the workspace, its connected accounts, contacts,
          conversations, automations, posts and analytics, and any user who belongs to no other
          workspace.
        </li>
      </ul>

      <h2>3. Email us</h2>
      <p>
        Write to <Mail /> from the email address on your AutoDM account (or name the Instagram
        account concerned) with the subject “Data deletion request”. We confirm within 48 hours.
      </p>

      <h2>What we delete</h2>
      <p>
        The connected Instagram account and its encrypted access token, media we synced, comments,
        story replies and direct messages, contacts and their details, automations and their run
        history, scheduled posts, and related analytics. Deletion is immediate in our database and
        complete in backups within 30 days. We keep only what the law requires (for example
        invoices) and a record that the deletion happened.
      </p>
    </LegalPage>
  );
}

/** LEG-04 */
export function ContactPage() {
  return (
    <LegalPage title="Contact">
      <p>
        AutoDM is operated by <strong>{OPERATOR}</strong>.
      </p>
      <ul>
        <li>
          Support, billing and privacy: <Mail />
        </li>
        <li>
          Website: <a href={SITE_URL}>{SITE_URL.replace('https://', '')}</a>
        </li>
      </ul>
      <p>
        We answer within one business day. For data deletion, see the{' '}
        <Link to="/data-deletion">data deletion page</Link>.
      </p>
    </LegalPage>
  );
}

interface DeletionStatus {
  readonly code: string;
  readonly status: 'completed';
  readonly receivedAt: string;
  readonly completedAt: string | null;
  readonly accountsErased: number;
}

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' }) : '—';

/** LEG-09 / LEG-10 */
export function DeletionStatusPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const q = useQuery<DeletionStatus, ApiError>({
    queryKey: ['deletion-status', code],
    enabled: code !== '',
    queryFn: () =>
      unwrap(api.get<DeletionStatus>(`/webhooks/meta/data-deletion/${encodeURIComponent(code)}`)),
  });
  const unknown = (
    <p>
      We could not find a deletion request with this code. Check the link you were given, or write
      to <Mail /> and we will help.
    </p>
  );
  return (
    <LegalPage title="Data Deletion Status">
      {code === '' ? (
        unknown
      ) : q.isLoading ? (
        <p className="muted">Looking up your request…</p>
      ) : q.isError || !q.data ? (
        unknown
      ) : (
        <>
          <p>
            {q.data.accountsErased > 0
              ? `Your Instagram data has been deleted from AutoDM (${q.data.accountsErased} connected account${q.data.accountsErased === 1 ? '' : 's'} and everything linked to it).`
              : 'AutoDM held no data for this Instagram account, so there was nothing to delete.'}
          </p>
          <table className="legal-status">
            <tbody>
              <tr>
                <th>Confirmation code</th>
                <td>
                  <code>{q.data.code}</code>
                </td>
              </tr>
              <tr>
                <th>Status</th>
                <td>Completed</td>
              </tr>
              <tr>
                <th>Received</th>
                <td>{when(q.data.receivedAt)}</td>
              </tr>
              <tr>
                <th>Completed</th>
                <td>{when(q.data.completedAt)}</td>
              </tr>
            </tbody>
          </table>
          <p className="muted small">
            Backups that may still hold a copy roll over within 30 days. Questions: <Mail />.
          </p>
        </>
      )}
    </LegalPage>
  );
}
